import { Alert, Button, DatePicker, Form, Input, InputNumber, Select, Space, message } from 'antd';
import moment from 'moment';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { api } from '@/api';
import { Drawer } from '@/components/drawer';
import { showError } from '@/utils/common';
import { getTranslatedString } from '@/utils/intl';

const CROP_PHASES: cyrian.api.documentImports.CropPhase[] = ['Principale', 'Intermédiaire', 'Petite', 'NONE'];
const CERTIFICATIONS: cyrian.api.documentImports.Certification[] = [
  'COCOALIFE',
  'RAINFOREST',
  'FAIRTRADE',
  'CACAO PREMIUS CYRIAN',
  'NONE',
];
const DATE_FORMAT = 'YYYY-MM-DD';

interface FormValues {
  waybillNumber?: string | null;
  cooperativeCode?: string | null;
  cooperativeName?: string | null;
  port?: string | null;
  campaign?: string | null;
  originDepartment?: string | null;
  cropPhase?: cyrian.api.documentImports.CropPhase | null;
  certification?: cyrian.api.documentImports.Certification | null;
  bagCount?: number | null;
  netWeightKg?: number | null;
  transportCostFcfa?: number | null;
  // Antd DatePicker uses moment internally; we convert on submit.
  editionDate?: moment.Moment | null;
}

interface ReviewDrawerProps {
  importRow: cyrian.api.documentImports.DocumentImport | null;
  onClose: () => void;
  onSubmitted: () => void;
  /** Trigger a list refetch on the page so `importRow` reflects the latest
   *  server state. Called after a Submit failure so the entity_not_found
   *  banner re-renders with the new error (otherwise the operator sees the
   *  same stale message and has to close + reopen the drawer). */
  onRefresh: () => Promise<void> | void;
}

// Visible to the operator next to fields the OCR pipeline flagged as low
// confidence — gives a per-field tooltip without inventing new visuals.
const uncertainProps = (uncertain: Set<string>, field: string) =>
  uncertain.has(field)
    ? {
        validateStatus: 'warning' as const,
        help: getTranslatedString('waybills.imports.review.uncertain_field_tooltip'),
      }
    : {};

const toFormValues = (parsed: cyrian.api.documentImports.WaybillPdfParsed | null | undefined): FormValues => {
  if (!parsed) return {};
  return {
    waybillNumber: parsed.waybillNumber ?? null,
    cooperativeCode: parsed.cooperativeCode ?? null,
    cooperativeName: parsed.cooperativeName ?? null,
    port: parsed.port ?? null,
    campaign: parsed.campaign ?? null,
    originDepartment: parsed.originDepartment ?? null,
    cropPhase: parsed.cropPhase ?? null,
    certification: parsed.certification ?? null,
    bagCount: parsed.bagCount ?? null,
    netWeightKg: parsed.netWeightKg ?? null,
    transportCostFcfa: parsed.transportCostFcfa ?? null,
    // Tolerate either an ISO string or an already-bad value; moment(null) is
    // an invalid date and Antd's DatePicker renders that as empty — exactly
    // what we want when OCR returned null.
    editionDate: parsed.editionDate ? moment(parsed.editionDate, DATE_FORMAT) : null,
  };
};

// When the OCR pipeline didn't self-flag a field but persistence failed
// because a referenced master-data row wasn't found, infer the failing
// fields from the backend error string. This is what lets us highlight a
// wrong `cooperativeCode` even though Gemini confidently extracted it (the
// empirical test showed Gemini won't always self-flag a smudged code).
//
// Backend collects ALL three master-data resolver errors per Submit and
// concatenates them (see `WaybillService.upsertFromPdf` Promise.allSettled
// + notFoundMessages.join). So we scan for every pattern and return every
// matching field name — that way the drawer paints the operator a single
// picture of everything that needs fixing instead of forcing one
// Submit-per-error cycle.
//
// Patterns come from `src/common/utils/waybill-resolvers.util.ts`. Regex
// (not exact string match) so we tolerate minor wording tweaks.
const inferFailingFields = (error: string | null | undefined): string[] => {
  if (!error) return [];
  const fields: string[] = [];
  // Anchored on the resolver's "not found" phrasing (see
  // `Cyrian-Backend/src/common/utils/waybill-resolvers.util.ts`) so we don't
  // false-positive on unrelated mentions like "cooperative_id" in some
  // future validation error or "harbor service unavailable" in an infra
  // failure. Each resolver emits exactly one message of the form
  // `<Entity> "<value>" not found …`, so the noun + "not found" anchor is
  // both specific and stable.
  if (/cooperative\b[^.]*not found/i.test(error)) fields.push('cooperativeCode');
  if (/harbor\b[^.]*not found/i.test(error)) fields.push('port');
  if (/campaign\b[^.]*not found/i.test(error)) fields.push('campaign');
  return fields;
};

// Pre-check normalization for inline-create dedup. Matches the backend
// `normalizeForMatch` helper (NFD decompose → strip combining diacritics
// → lowercase → trim). Without this, an operator typing "abc123" while the
// DB holds "ABC123" would slip past the duplicate check; the create POST
// would then either hit a 500 (DB-side case-sensitive unique constraint)
// or, worse, create a case-variant duplicate.
const normalizeForMatch = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

// After the operator clicks "Create cooperative" / "Create harbor", the
// underlying problem is resolved client-side. Strip the matching sentence(s)
// from the entity-not-found banner so the visible error reflects only what
// still needs the operator's attention. The backend's combined error joins
// resolver messages with single spaces between sentences ending in '.',
// hence the lookbehind on the period.
const FIELD_TO_ERROR_KEYWORD: Record<string, RegExp> = {
  cooperativeCode: /cooperative/i,
  port: /harbor/i,
  campaign: /campaign/i,
};

const filterErrorByResolved = (error: string | null | undefined, resolved: Set<string>): string => {
  if (!error || resolved.size === 0) return error ?? '';
  const sentences = error.split(/(?<=\.)\s+/);
  const kept = sentences.filter((s) => {
    for (const field of resolved) {
      const re = FIELD_TO_ERROR_KEYWORD[field];
      if (re?.test(s)) return false;
    }
    return true;
  });
  return kept.join(' ').trim();
};

const toApiPayload = (values: FormValues): cyrian.api.documentImports.WaybillPdfParsed => ({
  waybillNumber: values.waybillNumber || null,
  cooperativeCode: values.cooperativeCode || null,
  cooperativeName: values.cooperativeName || null,
  port: values.port || null,
  campaign: values.campaign || null,
  originDepartment: values.originDepartment || null,
  cropPhase: values.cropPhase || null,
  certification: values.certification || null,
  bagCount: typeof values.bagCount === 'number' ? values.bagCount : null,
  netWeightKg: typeof values.netWeightKg === 'number' ? values.netWeightKg : null,
  transportCostFcfa: typeof values.transportCostFcfa === 'number' ? values.transportCostFcfa : null,
  editionDate: values.editionDate ? values.editionDate.format(DATE_FORMAT) : null,
});

export const ReviewDrawer: React.FC<ReviewDrawerProps> = ({ importRow, onClose, onSubmitted, onRefresh }) => {
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [creatingCooperative, setCreatingCooperative] = useState(false);
  const [creatingHarbor, setCreatingHarbor] = useState(false);
  // Fields the operator just resolved client-side via inline Create. These
  // drop out of the highlighted set and out of the banner text until the
  // next Submit refreshes `importRow.error` from the server. Reset on each
  // open of the drawer (different row) so a previous session's resolutions
  // don't leak across imports.
  const [resolvedFields, setResolvedFields] = useState<Set<string>>(new Set());

  // Reset form whenever a *different* row is opened. Dep on `importRow?.id`
  // (not the whole object) so a same-row refresh (e.g. after a failed Submit
  // updates `importRow.error` from the store) does NOT wipe the operator's
  // in-progress edits or the resolvedFields set — only the banner/highlights
  // re-derive, which is what we want.
  useEffect(() => {
    if (importRow) {
      form.resetFields();
      form.setFieldsValue(toFormValues(importRow.parsedData));
      setResolvedFields(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importRow?.id, form]);

  // Two sources of "needs operator attention" highlighting on a field:
  //   1. OCR self-flag (`_uncertainFields` array on parsedData).
  //   2. Persistence error blamed on a specific field (parsed out of
  //      `error`). Path 2 covers the empirically-confirmed case where Gemini
  //      confidently misreads a smudged code AND that wrong code doesn't
  //      exist in master data — without it the drawer renders with no visual
  //      cue and the operator could click "Create cooperative" on a misread.
  const uncertain = useMemo<Set<string>>(() => {
    const set = new Set<string>(importRow?.parsedData?._uncertainFields ?? []);
    const hasOcrUncertainty = set.size > 0;
    if (!hasOcrUncertainty && importRow?.status === 'review') {
      for (const field of inferFailingFields(importRow.error)) {
        set.add(field);
      }
    }
    // Drop anything the operator already resolved via inline Create.
    for (const field of resolvedFields) set.delete(field);
    return set;
  }, [importRow, resolvedFields]);

  const onSubmit = async (values: FormValues) => {
    if (!importRow) return;
    setSubmitting(true);
    try {
      await api.documentImports.retry(importRow.id, { parsedData: toApiPayload(values) }).source;
      message.success(getTranslatedString('waybills.imports.review.submit_success'));
      onSubmitted();
      onClose();
    } catch (err) {
      showError(err);
      // Refresh so the next render gets the up-to-date `importRow.error`
      // — backend writes the new (possibly different) set of missing
      // entities into the row before responding, and the operator should
      // see the updated banner without having to close + reopen the drawer.
      await onRefresh();
    } finally {
      setSubmitting(false);
    }
  };

  // Inline cooperative create: triggered by the button next to the
  // cooperativeCode field. The operator must have already filled in code AND
  // name — this is the safety against the original bug, where Gemini's
  // hallucinated code was mistaken for a real new cooperative.
  //
  // Pre-check duplicate via `getAll({ code })` instead of relying on the
  // backend POST: the cooperatives endpoint surfaces unique-constraint
  // violations as bare 500s ("server error"), which is opaque to the operator
  // ("did anything happen? do I have to try again? did it just create a
  // duplicate?"). A read-then-write loses race-safety in theory, but for the
  // operator-driven inline-create flow it's the right ergonomics.
  const onCreateCooperative = async () => {
    const code = form.getFieldValue('cooperativeCode') as string | null | undefined;
    const name = form.getFieldValue('cooperativeName') as string | null | undefined;
    if (!code || !name) {
      message.error(getTranslatedString('waybills.imports.review.cooperative.create_missing'));
      return;
    }
    setCreatingCooperative(true);
    const codeKey = normalizeForMatch(code);
    try {
      // limit: 200 — well above the realistic count of cooperatives that
      // could collide on a normalized code. Backend may filter `code`
      // server-side (exact/ILIKE varies by endpoint version) — final
      // compare is client-side via the same normalize helper the backend
      // resolver applies. Without this, an operator typing "abc123" would
      // miss the existing "ABC123" and create a case-variant duplicate.
      const existing = await api.cooperatives.getAll({ code, limit: 200 }).source;
      const dup = existing?.data?.items?.find(
        (c: cyrian.api.cooperatives.Cooperative) => c.code != null && normalizeForMatch(c.code) === codeKey,
      );
      if (dup) {
        // The cooperative is already in the DB — same effect as a fresh
        // create from the operator's POV. Mark resolved so the highlight
        // and banner text update immediately.
        setResolvedFields((prev) => new Set(prev).add('cooperativeCode'));
        message.warning(getTranslatedString('waybills.imports.review.cooperative.already_exists', { code }));
        return;
      }
      await api.cooperatives.create({ name, code }).source;
      setResolvedFields((prev) => new Set(prev).add('cooperativeCode'));
      message.success(getTranslatedString('waybills.imports.review.cooperative.create_success', { code }));
    } catch (err) {
      showError(err);
    } finally {
      setCreatingCooperative(false);
    }
  };

  // Inline harbor create. Same pattern as cooperative — operator must have
  // filled in the port name. Backend's `resolveHarborByNameStrict` matches
  // diacritic + case-insensitively via `normalizeForMatch`, so the pre-check
  // here uses the same helper to stay consistent: "San-Pédro" vs "SAN-PEDRO"
  // must show "already exists", not double-create.
  const onCreateHarbor = async () => {
    const port = form.getFieldValue('port') as string | null | undefined;
    if (!port) {
      message.error(getTranslatedString('waybills.imports.review.harbor.create_missing'));
      return;
    }
    setCreatingHarbor(true);
    const portKey = normalizeForMatch(port);
    try {
      // limit: 200 — well above the realistic harbor count; we don't trust
      // the backend `name` filter to be normalize-equivalent, so we filter
      // client-side against the full page. Without an explicit limit the
      // default page size could clip the result.
      const existing = await api.addresses.getAll({ type: 'Harbor', name: port, limit: 200 }).source;
      const dup = existing?.data?.items?.find(
        (a: cyrian.api.addresses.Address) => normalizeForMatch(a.name) === portKey,
      );
      if (dup) {
        setResolvedFields((prev) => new Set(prev).add('port'));
        message.warning(getTranslatedString('waybills.imports.review.harbor.already_exists', { name: port }));
        return;
      }
      // Backend's AddressCreateDto allows country/city/street/street2 as optional;
      // the TS frontend DTO marks them required `string`, so send empty strings
      // to satisfy both layers without prompting the operator for extra fields.
      await api.addresses.create({
        name: port,
        type: 'Harbor',
        country: '',
        city: '',
        street: '',
        street2: '',
      }).source;
      setResolvedFields((prev) => new Set(prev).add('port'));
      message.success(getTranslatedString('waybills.imports.review.harbor.create_success', { name: port }));
    } catch (err) {
      showError(err);
    } finally {
      setCreatingHarbor(false);
    }
  };

  // Banner picks its copy based on the *source* of the issue, not whether
  // any field happens to be highlighted: OCR self-flagged → "low_confidence",
  // resolver miss → "entity_not_found" (warns about misread codes since
  // inline-create on a wrong code is the original-bug pattern).
  //
  // After the operator resolves a field via inline Create, the corresponding
  // sentence in the entity_not_found error is stripped so the visible text
  // reflects only outstanding problems. The whole banner is hidden if
  // nothing is left to show — operator sees a clean form ready to Submit.
  const ocrUncertainOutstanding = (importRow?.parsedData?._uncertainFields ?? []).some((f) => !resolvedFields.has(f));
  const showLowConfidenceBanner = ocrUncertainOutstanding;
  const bannerKey = showLowConfidenceBanner
    ? 'waybills.imports.review.banner.low_confidence'
    : 'waybills.imports.review.banner.entity_not_found';
  const bannerDescription = showLowConfidenceBanner
    ? importRow?.parsedData?._extractionNotes || undefined
    : filterErrorByResolved(importRow?.error, resolvedFields) || undefined;
  const showBanner = uncertain.size > 0 || Boolean(bannerDescription);

  return (
    <Drawer
      visible={Boolean(importRow)}
      onClose={onClose}
      title={getTranslatedString('waybills.imports.review.title')}
      width={640}
      footer={(close) => (
        <div className="drawer-footer">
          <Button className="drawer-footer-button" onClick={close}>
            <FormattedMessage id="waybills.imports.review.cancel" />
          </Button>
          <Button type="primary" loading={submitting} onClick={() => form.submit()}>
            <FormattedMessage id="waybills.imports.review.submit" />
          </Button>
        </div>
      )}
    >
      {importRow && (
        <>
          {showBanner && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={<FormattedMessage id={bannerKey} />}
              description={bannerDescription}
            />
          )}
          {/* `initialValues` intentionally omitted: the Antd `<Form>` reads
              it once on mount, but the drawer-wrapped form stays mounted
              across opens — pre-fill happens via `form.setFieldsValue` in
              the useEffect above. */}
          <Form<FormValues> form={form} layout="vertical" onFinish={onSubmit}>
            <Form.Item
              name="waybillNumber"
              label={<FormattedMessage id="waybills.waybillNumber" />}
              rules={[{ required: true, message: getTranslatedString('waybills.imports.review.required') }]}
              {...uncertainProps(uncertain, 'waybillNumber')}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="cooperativeCode"
              label={<FormattedMessage id="waybills.imports.review.cooperativeCode" />}
              rules={[{ required: true, message: getTranslatedString('waybills.imports.review.required') }]}
              {...uncertainProps(uncertain, 'cooperativeCode')}
            >
              <Input
                addonAfter={
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      if (!creatingCooperative) void onCreateCooperative();
                    }}
                  >
                    {creatingCooperative ? (
                      '…'
                    ) : (
                      <FormattedMessage id="waybills.imports.review.cooperative.create_inline" />
                    )}
                  </a>
                }
              />
            </Form.Item>
            <Form.Item
              name="cooperativeName"
              label={<FormattedMessage id="waybills.imports.review.cooperativeName" />}
              // Required to keep parity with `onCreateCooperative` which
              // refuses to POST without a name. Backend technically tolerates
              // a null name (falls back to using `code` as display) but the
              // inline-create flow is a no-go without a name typed in.
              rules={[{ required: true, message: getTranslatedString('waybills.imports.review.required') }]}
              {...uncertainProps(uncertain, 'cooperativeName')}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="port"
              label={<FormattedMessage id="waybills.harbor" />}
              rules={[{ required: true, message: getTranslatedString('waybills.imports.review.required') }]}
              {...uncertainProps(uncertain, 'port')}
            >
              <Input
                addonAfter={
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      if (!creatingHarbor) void onCreateHarbor();
                    }}
                  >
                    {creatingHarbor ? '…' : <FormattedMessage id="waybills.imports.review.harbor.create_inline" />}
                  </a>
                }
              />
            </Form.Item>
            <Form.Item
              name="campaign"
              label={<FormattedMessage id="waybills.campaign" />}
              rules={[{ required: true, message: getTranslatedString('waybills.imports.review.required') }]}
              {...uncertainProps(uncertain, 'campaign')}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="originDepartment"
              label={<FormattedMessage id="waybills.imports.review.originDepartment" />}
              {...uncertainProps(uncertain, 'originDepartment')}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="cropPhase"
              label={<FormattedMessage id="waybills.imports.review.cropPhase" />}
              rules={[{ required: true, message: getTranslatedString('waybills.imports.review.required') }]}
              {...uncertainProps(uncertain, 'cropPhase')}
            >
              <Select>
                {CROP_PHASES.map((value) => (
                  <Select.Option key={value} value={value}>
                    {value}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="certification"
              label={<FormattedMessage id="waybills.imports.review.certification" />}
              rules={[{ required: true, message: getTranslatedString('waybills.imports.review.required') }]}
              {...uncertainProps(uncertain, 'certification')}
            >
              <Select>
                {CERTIFICATIONS.map((value) => (
                  <Select.Option key={value} value={value}>
                    {value}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Space style={{ display: 'flex' }} size="middle">
              <Form.Item
                name="bagCount"
                label={<FormattedMessage id="waybills.imports.review.bagCount" />}
                rules={[{ required: true, message: getTranslatedString('waybills.imports.review.required') }]}
                {...uncertainProps(uncertain, 'bagCount')}
                style={{ flex: 1 }}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="netWeightKg"
                label={<FormattedMessage id="waybills.imports.review.netWeightKg" />}
                rules={[{ required: true, message: getTranslatedString('waybills.imports.review.required') }]}
                {...uncertainProps(uncertain, 'netWeightKg')}
                style={{ flex: 1 }}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Space>
            <Form.Item
              name="transportCostFcfa"
              label={<FormattedMessage id="waybills.imports.review.transportCostFcfa" />}
              {...uncertainProps(uncertain, 'transportCostFcfa')}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="editionDate"
              label={<FormattedMessage id="waybills.imports.review.editionDate" />}
              rules={[{ required: true, message: getTranslatedString('waybills.imports.review.required') }]}
              {...uncertainProps(uncertain, 'editionDate')}
            >
              <DatePicker style={{ width: '100%' }} format={DATE_FORMAT} />
            </Form.Item>
          </Form>
        </>
      )}
    </Drawer>
  );
};
